import {useNuxtApp} from 'nuxt/app'
import {useUserStore} from '@/store/user'
import {useAuthStore} from '@/store/auth'
import {useTargetedMessagesStore} from '@/store/targeted-messages'
import {useKeycloakService} from '~/composables/keycloak_service'
import * as Sentry from '@sentry/browser'
import type {AxiosError} from 'axios'
import type {KeycloakConfig} from './types'
import {restoreUserStoreFromAccessToken, buildKeycloakUser} from './useKeycloakToken'
import {clearKeycloakState, getKeycloakHomePath, resolveRedirectPath} from './useKeycloakSession'
import {useKeycloakUser} from './useKeycloakUser'

async function loginWithKeycloak(returnPath?: string) {
  if (!process.client) {return}

  try {
    const app = useNuxtApp()
    const userManager = app.$userManager

    const state = {
      signInRedirect: returnPath ?? '/eu/en',
      locale: app.$i18n?.locale?.value ?? 'en',
      region: app.$globalization?.getRegion() ?? 'eu'
    }

    await userManager.signinRedirect({
      state,
      extraQueryParams: {
        region: state.region,
        ui_locales: state.locale
      }
    })
  } catch (error) {
    Sentry.captureException(error, {tags: {source: 'keycloak_login'}})
  }
}

async function handleKeycloakCallback(): Promise<string | null> {
  if (!process.client) {
    return null
  }

  const app = useNuxtApp()
  const runtimeConfig = useRuntimeConfig()
  const {fetchKeycloakUser} = useKeycloakUser()

  try {
    const userManager = app.$userManager

    const urlParams = new URLSearchParams(globalThis.location.search)
    const code = urlParams.get('code')
    const state = urlParams.get('state')

    const authenticationExpired = urlParams.get('error') === 'temporarily_unavailable'
      && urlParams.get('error_description') === 'authentication_expired'

    if (!authenticationExpired && (!code || !state)) {
      return null
    }

    const stateKey = `ENuxtKeycloakState.${state}`
    const storedStateJson = localStorage.getItem(stateKey)

    if (!storedStateJson || authenticationExpired) {
      return getKeycloakHomePath()
    }

    const storedState = JSON.parse(storedStateJson)
    const codeVerifier = storedState.code_verifier
    const redirectUri = storedState.redirect_uri ?? runtimeConfig.public.BASE_URL + '/auth'

    if (!codeVerifier) {
      Sentry.captureMessage('Code verifier not found in stored state', 'warning')
      return null
    }

    localStorage.removeItem(stateKey)

    const keycloakService = useKeycloakService()
    const authStore = useAuthStore()
    const userStore = useUserStore()

    if (userStore.customerUuidLink && userStore.customerUuidLink !== 'default') {
      // @ts-ignore - userStore.newCustomer can accept string
      userStore.newCustomer = userStore.customerUuidLink
    }
    userStore.customerUuidLink = ''

    const customerUuid = userStore.newCustomer ?? userStore.selectedCustomer?.customerUuid ?? 'default'

    let response
    try {
      response = await keycloakService.exchangeKeycloakCode(customerUuid, code, codeVerifier, redirectUri)
    } catch (error) {
      if ((error as AxiosError).response?.status === 400) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {source: 'keycloak_code_exchange'}
        })
        return redirectToAccessDenied(app)
      }
      throw error
    }

    if (!response?.data?.accessToken) {
      Sentry.captureMessage('No access token received from backend', 'warning')
      return redirectToAccessDenied(app)
    }

    restoreUserStoreFromAccessToken(response.data.accessToken)
    const user = buildKeycloakUser(
      response.data.accessToken,
      response.data.idToken,
      response.data.refreshToken,
      storedState.data
    )

    await userManager.storeUser(user)
    authStore.setUserObject(user)

    const targetedMessagesStore = useTargetedMessagesStore()
    targetedMessagesStore.resetTemporaryDismissals()

    await fetchKeycloakUser(userManager)

    return resolveRedirectPath(storedState.data?.signInRedirect)
  } catch (error) {
    Sentry.captureException(error, {tags: {source: 'keycloak_callback'}})
    return redirectToAccessDenied(app)
  }
}

// Backend code exchange failed after a successful Keycloak login (e.g. user not
// in backend). Clear local state and send the user to the no-access page, where
// a button lets them terminate the lingering Keycloak SSO session.
function redirectToAccessDenied(app: any): string {
  // Best-effort local cleanup; the Keycloak SSO session lives until the user
  // terminates it from the no-access page.
  clearKeycloakState(app.$userManager).catch((error: unknown) => {
    Sentry.captureException(error, {tags: {source: 'keycloak_orphaned_session_cleanup'}})
  })

  let accessDeniedPath = '/access-denied'
  try {
    accessDeniedPath = app.localePath('/access-denied', null, null)
  } catch (_) {
    // fall back to the non-localized path
  }

  return `${accessDeniedPath}?orphanedSession=keycloak`
}

async function handleKeycloakRestoration(to: any) {
  if (!process.client) {return}

  const {fetchKeycloakUser} = useKeycloakUser()
  const authStore = useAuthStore()
  const userStore = useUserStore()

  const needsRestore = !authStore.isTokenExpired
    && authStore?.userObject
    && userStore?.userUuid === ''
    && !to.fullPath.startsWith('/auth#state')
    && !to.fullPath.startsWith('/auth?')

  if (needsRestore) {
    const app = useNuxtApp()
    const userManager = app.$userManager

    try {
      const storedUser = await userManager.getUser()

      if (!storedUser || !storedUser.access_token) {
        Sentry.captureMessage('No stored user found during Keycloak restoration', 'warning')
        await clearKeycloakState(userManager)
        return
      }

      restoreUserStoreFromAccessToken(storedUser.access_token)

      await fetchKeycloakUser(userManager)
    } catch (error) {
      Sentry.captureException(error, {tags: {source: 'keycloak_restoration'}})
      await clearKeycloakState(userManager)
    }
  }

  if (authStore.userObject && authStore.isTokenExpired) {
    const app = useNuxtApp()
    const userManager = app.$userManager
    await clearKeycloakState(userManager)
  }
}

async function logoutFromKeycloak(options?: {postLogoutRedirectUri?: string}) {
  if (!process.client) {return}

  try {
    const app = useNuxtApp()
    const userManager = app.$userManager

    const user = await userManager.getUser()
    const config = useRuntimeConfig().public
    const keycloakConfig = config.keycloak as KeycloakConfig
    const baseUrl = config.BASE_URL

    const postLogoutRedirectUri = options?.postLogoutRedirectUri ?? baseUrl + '/'

    await clearKeycloakState(userManager)

    const logoutUrl = `${keycloakConfig.authority}/protocol/openid-connect/logout`
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = logoutUrl

    const redirectInput = document.createElement('input')
    redirectInput.type = 'hidden'
    redirectInput.name = 'post_logout_redirect_uri'
    redirectInput.value = postLogoutRedirectUri
    form.appendChild(redirectInput)

    if (keycloakConfig.clientId) {
      const clientInput = document.createElement('input')
      clientInput.type = 'hidden'
      clientInput.name = 'client_id'
      clientInput.value = keycloakConfig.clientId
      form.appendChild(clientInput)
    }

    if (user?.id_token) {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'id_token_hint'
      tokenInput.value = user.id_token
      form.appendChild(tokenInput)
    }

    document.body.appendChild(form)
    form.submit()
  } catch (error) {
    Sentry.captureException(error, {tags: {source: 'keycloak_logout'}})
    const app = useNuxtApp()
    globalThis.location.href = app.localePath('/', null, null)
  }
}

async function handleKeycloakBeforeMount(): Promise<void> {
  const app = useNuxtApp()
  const authStore = useAuthStore()
  const {CookieStorage} = await import('cookie-storage')
  const cookieStorage = new CookieStorage({
    expires: new Date(Date.now() + 3600 * 1000),
    path: '/'
  })

  const existingUser = await app.$userManager.getUser()

  if (existingUser && existingUser.expired) {
    await app.$userManager.removeUser()
    cookieStorage.removeItem('ENuxtKeycloakUser.sub')
  } else if (existingUser) {
    if (app.payload?.serverRendered) {
      if (existingUser.profile?.sub) {
        cookieStorage.setItem('ENuxtKeycloakUser.sub', existingUser.profile.sub)
      }
      // eslint-disable-next-line no-console
      console.log('ssr mismatch, reloading')
      const {reloadNuxtApp} = await import('nuxt/app')
      reloadNuxtApp()
    } else {
      if (authStore.userFetchInProgress) {
        return
      }
      await handleKeycloakRestoration({fullPath: globalThis.location.pathname + globalThis.location.hash})
    }
  }
}

export const useKeycloakAuth = () => {
  return {
    loginWithKeycloak,
    handleKeycloakCallback,
    handleKeycloakRestoration,
    logoutFromKeycloak,
    handleKeycloakBeforeMount
  }
}
